
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment';
import { namespace } from 'vuex-class';
import { AxiosError } from 'axios';
import DollarInput from '@/core/components/DollarInput.vue';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { Prop } from 'vue-property-decorator';
import { Alert } from '@/core/types/alert';
import { AlertTypeEnum } from '@/core/types/alert-type-enum';
import { LoanDetails } from '@/loan/types/loan-details';
import { LoanRecurringPayment } from '@/loan/types/loan-recurring-payment';
import { LoanFrequency } from '@/loan/enums/loan-frequency';
import { LoanRecurringPaymentAvailableDates } from '@/loan/types/loan-recurring-payment-available-dates';
import { CoreHelper } from '@/core/helper';
import { LoanHelper } from '@/loan/helper';
import { FORM_DEFAULT_SELECT_OPTION } from '@/core/constants/form-default-select-option';
import { FormSelectOption } from '@/core/types/form-select-option';
import LoanAutopayConfirmModal from '@/loan/components/LoanAutopayConfirmModal.vue';
import { LoanAutopayConfirmInput } from '@/loan/types/loan-autopay-confirm-input';
import { ModalState } from '@/core/types/modal-state';
import LoanAutopaySkipConfirmModal from '@/loan/components/LoanAutopaySkipConfirmModal.vue';
import LoanAutopayDisableModal from '@/loan/components/LoanAutopayDisableModal.vue';
import { SessionUser } from '@/core/types/session-user';
import { LoanAPIService } from '@/loan/services/api';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');
const coreNamespace = namespace('core');
const BACK_BUTTON_TEXT_HAS_SEEN_LOAN_INTRO = 'Back to Loan';
const BACK_BUTTON_TEXT_HAS_NOT_SEEN_LOAN_INTRO = 'Continue';
const CURRENT_PAYMENT_TEXT = (amount: number) => `Current Automatic Payment: ${UtilHelper.formatDollar(amount)}`;
const DEFAULT_ERROR_MESSAGE = 'There was an error submitting your payment, please try again.';
const GREATER_THAN_PAYOFF_AMOUNT_TEXT = (max: number) =>
	`The maximum monthly payment amount is ${UtilHelper.formatDollar(max)}.`;
const LESS_THAN_MINIMUM_PAYMENT_AMOUNT_TEXT = 'New amount should be at or above your minimum monthly payment.';
const SUBMITTED_PAYMENT_BUTTON_TURN_ON_TEXT = 'Turn On Automatic Payments';
const SUBMITTED_PAYMENT_BUTTON_UPDATE_TEXT = 'Set New Automatic Payment';
const SUBMITTED_PAYMENT_SUCCESS_MESSAGE = 'Your monthly recurring payment has been set.';
const MINIMUM_PAYMENT_TEXT = (amount: number) => `Minimum Payment: ${UtilHelper.formatDollar(amount)}`;
const today = new Date();
const tomorrow = new Date(today.getDate() + 1);

const TEST_ID = 'Kloans-Loan-LoanAutopay';

@Component({
	components: {
		DollarInput,
		LoanAutopayConfirmModal,
		LoanAutopaySkipConfirmModal,
		LoanAutopayDisableModal,
	},
})
export default class LoanAutopay extends Vue {
	public TEST_ID = TEST_ID;

	public alert: Alert | null = null;

	@Prop({ required: true })
	public amount!: number;

	public autopaySkipConfirmModalState: ModalState = { isShown: false };

	@loanNamespace.State('recurringPaymentAvailableDates')
	public availableDates!: LoanRecurringPaymentAvailableDates;

	public confirmModalCurrentInput: LoanAutopayConfirmInput | null = null;

	public confirmModalPreviousInput: LoanAutopayConfirmInput | null = null;

	public disableAutopayModalState: ModalState = { isShown: false, isLoading: false };

	public confirmModalState: ModalState = { isShown: false };

	@loanNamespace.Getter('daysDelinquent')
	public daysDelinquent!: number;

	public draftDateOneISOStringValue: string = UtilHelper.convertDateToISOString(tomorrow);

	public draftDateTwoISOStringValue: string | null = null;

	public formatDateForDisplay = UtilHelper.formatDateForDisplay;

	public formatDollar = UtilHelper.formatDollar;

	public fromAccountOptions: FormSelectOption[] = [];

	public fromAccountValue = FORM_DEFAULT_SELECT_OPTION.value;

	public frequencyOptions = LoanHelper.getFrequencySelectOptions();

	public frequencyValue: LoanFrequency = LoanFrequency.MONTHLY;

	@loanNamespace.Getter('isAutopayOn')
	public isAutopayOn!: boolean;

	public isDraftDateOnePickerShown = false;

	public isDraftDateTwoPickerShown = false;

	public isPaymentLoading = false;

	@loanNamespace.State('linkedAccounts')
	public linkedAccounts!: LoanLinkedAccount[];

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	public paymentValue = 0;

	@loanNamespace.State('recurringPayment')
	public recurringPayment!: LoanRecurringPayment | null;

	@loanNamespace.Action('getAndSetLoanDetails')
	public getAndSetLoanDetails!: (loanId: string) => void;

	@coreNamespace.Getter('user')
	public sessionUser!: SessionUser;

	public wasAutopayConfirmSuccessful = false;

	get backButtonText(): string {
		return this.hasSeenLoanIntro ? BACK_BUTTON_TEXT_HAS_SEEN_LOAN_INTRO : BACK_BUTTON_TEXT_HAS_NOT_SEEN_LOAN_INTRO;
	}

	get cutOffDateForDisplay(): string {
		const { cutoffDate } = this.loanDetails.recurringPayment;
		return UtilHelper.formatDateForDisplay(new Date(cutoffDate));
	}

	get cutOffTime(): string {
		const { cutoffDate } = this.loanDetails.recurringPayment;
		return moment(cutoffDate).format('hh:mm A');
	}

	get effectiveDateForDisplay(): string {
		const { effectiveDate } = this.loanDetails.recurringPayment;
		return UtilHelper.formatISOStringForDisplay(effectiveDate);
	}

	get hasSeenLoanIntro(): boolean {
		return this.sessionUser.hasSeenLoanIntro !== 0;
	}

	get initialFrequencyValue(): LoanFrequency {
		return this.recurringPayment ? this.recurringPayment.frequency : LoanFrequency.MONTHLY;
	}

	get isFrequencySemiMonthly(): boolean {
		return this.frequencyValue === LoanFrequency.SEMI_MONTHLY;
	}

	get isPaymentButtonDisabled(): boolean {
		const isFromAccountValid = this.fromAccountValue !== FORM_DEFAULT_SELECT_OPTION.value;
		const isDraftDateTwoValid = this.isFrequencySemiMonthly ? !!this.draftDateTwoISOStringValue : true;

		return !isFromAccountValid || !this.isPaymentValid || !isDraftDateTwoValid;
	}

	get isPaymentLessThanMax(): boolean {
		return this.paymentValue <= this.paymentMax;
	}

	get isPaymentMoreThanMin(): boolean {
		return this.paymentValue >= this.paymentMin;
	}

	get isPaymentValid(): boolean {
		return this.isPaymentLessThanMax && this.isPaymentMoreThanMin;
	}

	get paymentAmountByFrequency(): number {
		return LoanHelper.calculateAutopayAmountByFrequency({
			autopayAmount: this.paymentValue,
			frequency: this.frequencyValue as LoanFrequency,
		});
	}

	get frequencyText(): string {
		try {
			return LoanHelper.getAutopayPaymentFrequencyText({
				amount: this.paymentAmountByFrequency,
				draftDateOne: UtilHelper.convertISOStringToDate(this.draftDateOneISOStringValue),
				draftDateTwo: this.draftDateTwoISOStringValue
					? UtilHelper.convertISOStringToDate(this.draftDateTwoISOStringValue)
					: null,
				frequency: this.frequencyValue as LoanFrequency,
			});
		} catch (error) {
			console.error(error);
			return '';
		}
	}

	roundAndFormatAutoPaySetupAmount() {
		let totalPaymentAmount: number;

		switch (this.frequencyValue) {
			case LoanFrequency.WEEKLY:
				totalPaymentAmount = Number(this.paymentAmountByFrequency) * 4;
				break;
			case LoanFrequency.BI_WEEKLY: case LoanFrequency.SEMI_MONTHLY:
				totalPaymentAmount = Number(this.paymentAmountByFrequency) * 2;
				break;
			default:
				totalPaymentAmount = Number(this.paymentAmountByFrequency);
		}

		this.paymentValue = Number(totalPaymentAmount.toFixed(2));
	}

	get paymentButtonText(): string {
		return this.isAutopayOn ? SUBMITTED_PAYMENT_BUTTON_UPDATE_TEXT : SUBMITTED_PAYMENT_BUTTON_TURN_ON_TEXT;
	}

	get paymentErrorMessage(): string | null {
		if (!this.isPaymentMoreThanMin) {
			return LESS_THAN_MINIMUM_PAYMENT_AMOUNT_TEXT;
		}

		if (!this.isPaymentLessThanMax) {
			return GREATER_THAN_PAYOFF_AMOUNT_TEXT(this.paymentMax);
		}

		return null;
	}

	get paymentMessages(): string[] {
		return !this.recurringPayment
			? [MINIMUM_PAYMENT_TEXT(this.paymentMin)]
			: [CURRENT_PAYMENT_TEXT(Number(this.recurringPayment.paymentTotal)), MINIMUM_PAYMENT_TEXT(this.paymentMin)];
	}

	get paymentMax(): number {
		const maxNum = 3 * Number(this.loanDetails.general.installmentAmount);
		return Math.round(maxNum * 100) / 100;
	}

	get paymentMin(): number {
		return Number(this.loanDetails.general.installmentAmount);
	}

	public checkIsAccountDisabled(accountOption: FormSelectOption): boolean {
		const linkedAccount = this.linkedAccounts.find((account) => account.id === accountOption.value);

		if (!linkedAccount) {
			return true;
		}

		return !linkedAccount.verified;
	}

	public linkNewAccount() {
		this.$router.push({
			name: 'ProfileMyProfile',
			params: { modalIsOpen: 'true', paymentRefUrl: window.location.href },
		});
	}

	public handleDraftDateOneAllowedDates(date: string): boolean {
		let dates: string[] = [];

		if (this.frequencyValue === LoanFrequency.BI_WEEKLY) {
			dates = this.availableDates[LoanFrequency.BI_WEEKLY];
		}

		if (this.frequencyValue === LoanFrequency.MONTHLY) {
			dates = this.availableDates[LoanFrequency.MONTHLY];
		}

		if (this.frequencyValue === LoanFrequency.SEMI_MONTHLY) {
			dates = this.availableDates[LoanFrequency.SEMI_MONTHLY];
		}

		if (this.frequencyValue === LoanFrequency.WEEKLY) {
			dates = this.availableDates[LoanFrequency.WEEKLY];
		}

		return !!dates.find((d) => d === date);
	}

	public getDraftDateTwoAllowedDates(date: string): boolean {
		const draftDateOne = UtilHelper.convertISOStringToDate(this.draftDateOneISOStringValue);
		const draftDateTwo = UtilHelper.convertISOStringToDate(date);
		const isAfterDraftOne = draftDateTwo > draftDateOne;

		if (!isAfterDraftOne) {
			return false;
		}

		let dates: string[] = [];

		if (this.frequencyValue === LoanFrequency.BI_WEEKLY) {
			dates = this.availableDates[LoanFrequency.BI_WEEKLY];
		}

		if (this.frequencyValue === LoanFrequency.MONTHLY) {
			dates = this.availableDates[LoanFrequency.MONTHLY];
		}

		if (this.frequencyValue === LoanFrequency.SEMI_MONTHLY) {
			dates = this.availableDates[LoanFrequency.SEMI_MONTHLY];
		}

		if (this.frequencyValue === LoanFrequency.WEEKLY) {
			dates = this.availableDates[LoanFrequency.WEEKLY];
		}

		return !!dates.find((d) => d === date);
	}

	mounted(): void {
		this.fromAccountOptions = LoanHelper.getLinkedAccountsSelectOtions(this.linkedAccounts, FORM_DEFAULT_SELECT_OPTION);

		// auto select if only account AND verified
		if (this.linkedAccounts.length === 1 && this.linkedAccounts[0].verified) {
			this.fromAccountValue = this.fromAccountOptions[1]?.value;
		} else {
			this.fromAccountValue = FORM_DEFAULT_SELECT_OPTION.value;
		}

		this.frequencyValue = this.initialFrequencyValue;

		this.draftDateOneISOStringValue = LoanHelper.getAutopayInitialDraftDateOneISOString({
			availableDates: this.availableDates,
			frequency: this.frequencyValue,
			recurringPayment: this.recurringPayment,
		});

		this.draftDateTwoISOStringValue = LoanHelper.getAutopayDraftDateTwoISOString({
			availableDates: this.availableDates,
			draftDateOneISOString: this.draftDateOneISOStringValue,
			frequency: this.frequencyValue,
			recurringPayment: this.recurringPayment,
		});

		this.paymentValue = this.amount;
	}

	public async onAutopayConfirm(): Promise<void> {
		await this.setPayment();
	}

	public onAutopaySkipConfirmModalSkip(): void {
		this.$router.push({ name: 'LoanSummary', params: { loanId: this.loanId } });
	}

	public onDisableAutopay(): void {
		this.disableAutopayModalState = { isShown: true, isLoading: false };
	}

	public onDraftDateOneChange(date: string): void {
		if (this.draftDateTwoISOStringValue) {
			const draftDateOne = UtilHelper.convertISOStringToDate(date);
			const draftDateTwo = UtilHelper.convertISOStringToDate(this.draftDateTwoISOStringValue);

			if (draftDateTwo <= draftDateOne) {
				this.draftDateTwoISOStringValue = null;
			}
		}
	}

	public onFrequencyChange(frequency: LoanFrequency): void {
		const { draftDateOneISOString, draftDateTwoISOString } = LoanHelper.getAutopayDraftDateISOStringsByFrequency({
			availableDates: this.availableDates,
			daysDelinquent: this.daysDelinquent,
			frequency,
			paymentDueDateISOString: this.loanDetails.general.paymentDueDate,
			recurringPayment: this.recurringPayment,
		});

		this.draftDateOneISOStringValue = draftDateOneISOString;
		this.draftDateTwoISOStringValue = draftDateTwoISOString;
		this.roundAndFormatAutoPaySetupAmount();
	}

	public async onMakePayment(): Promise<void> {
		this.alert = null;

		if (this.recurringPayment) {
			this.showConfirmModal();
			return;
		}

		await this.setPayment();
	}

	public onSkip(): void {
		this.autopaySkipConfirmModalState = { isShown: true };
	}

	private async setPayment(): Promise<void> {
		this.isPaymentLoading = true;

		const draftDateOne = UtilHelper.convertISOStringToDate(this.draftDateOneISOStringValue);
		const draftDateOneUTCISOString = UtilHelper.convertDateToUTCISOString(draftDateOne);

		const draftDateTwo = this.draftDateTwoISOStringValue
			? UtilHelper.convertISOStringToDate(this.draftDateTwoISOStringValue)
			: null;

		const draftDateTwoUTCISOString = draftDateTwo && UtilHelper.convertDateToUTCISOString(draftDateTwo);

		const request: LoanRecurringPayment = {
			accountId: this.fromAccountValue,
			draftDateOne: draftDateOneUTCISOString,
			draftDateTwo: draftDateTwoUTCISOString,
			frequency: this.frequencyValue as LoanFrequency,
			paymentTotal: String(this.paymentValue),
			paymentTotalByFrequency: String(this.paymentAmountByFrequency),
		};

		try {
			await LoanAPIService.setRecurringPayment(this.loanId, request);
			this.wasAutopayConfirmSuccessful = true;

			this.alert = {
				type: AlertTypeEnum.SUCCESS,
				message: SUBMITTED_PAYMENT_SUCCESS_MESSAGE,
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			const validatedError = CoreHelper.validateAxiosError(axiosError, DEFAULT_ERROR_MESSAGE);

			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: validatedError.message,
			};

			this.wasAutopayConfirmSuccessful = false;
		} finally {
			this.isPaymentLoading = false;
		}
	}

	public async disableAutopay(): Promise<void> {
		this.disableAutopayModalState = { isShown: true, isLoading: true };

		try {
			await LoanAPIService.disableAutopay(this.loanId);
			await this.getAndSetLoanDetails(this.loanId);

			this.disableAutopayModalState = { isShown: false, isLoading: false };
		} catch (error) {
			console.error(error);

			this.disableAutopayModalState = { isShown: true, isLoading: false };
		}
	}

	private showConfirmModal(): void {
		const draftDateOne = UtilHelper.convertISOStringToDate(this.draftDateOneISOStringValue);
		const draftDateTwo = this.draftDateTwoISOStringValue
			? UtilHelper.convertISOStringToDate(this.draftDateTwoISOStringValue)
			: null;

		const confirmInput = LoanHelper.getAutopayConfirmInput({
			amount: this.paymentAmountByFrequency,
			draftDateOne,
			draftDateTwo,
			frequency: this.frequencyValue as LoanFrequency,
			recurringPayment: this.recurringPayment as LoanRecurringPayment,
		});

		this.confirmModalCurrentInput = confirmInput.current;
		this.confirmModalPreviousInput = confirmInput.previous;
		this.confirmModalState = { isShown: true };
	}
}
